import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Input, Modal, Space, Spin, Table, Tag, message } from "antd";
import { CalendarOutlined, DownloadOutlined, FilterOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import axios from "../axios";
import "../components/TutorDrawer.css";

const formatINR = (value) => `₹${(Number(value) || 0).toLocaleString("en-IN")}`;

const formatReportMonth = (month) => month.format("MMMM YYYY");

const mapTransactionsToRows = (transactions = []) =>
  transactions.map((item) => ({
    key: String(item.id),
    dealId: item.deal_id ?? item.order_id ?? "-",
    parentName: item.student_name || "Student",
    subjects: "-",
    status: "Credited",
    amountEarned: Number(item.amount) || 0,
    createdAt: item.created_at || null,
    date: item.created_at
      ? new Date(item.created_at).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-",
    teachingHours: "-",
    fees: "-",
    transferType: item.transfer_type || "",
    orderId: item.order_id || "",
  }));

const filterTransactionsByMonth = (transactions, month) => {
  if (!month) return transactions;
  return transactions.filter((item) => {
    if (!item.created_at) return false;
    const date = dayjs(item.created_at);
    return date.year() === month.year() && date.month() === month.month();
  });
};

const sumAmount = (transactions) =>
  transactions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

const escapeCsvCell = (value) => {
  const text = String(value ?? "");
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadCsv = (rows, filename) => {
  const headers = [
    "Deal ID",
    "Parent Name",
    "Subjects",
    "Status",
    "Amount Earned",
    "Date",
    "Teaching Hours",
    "Fees",
    "Transfer Type",
    "Order ID",
  ];
  const lines = [
    headers.join(","),
    ...rows.map((row) =>
      [
        row.dealId,
        row.parentName,
        row.subjects,
        row.status,
        row.amountEarned,
        row.date,
        row.teachingHours,
        row.fees,
        row.transferType,
        row.orderId,
      ]
        .map(escapeCsvCell)
        .join(","),
    ),
  ];
  const blob = new Blob([`\uFEFF${lines.join("\n")}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function TutorEarningsReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appliedMonth, setAppliedMonth] = useState(null);
  const [draftMonth, setDraftMonth] = useState(dayjs());
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const allTransactions = useMemo(
    () => earnings?.transactions || earnings?.items || [],
    [earnings],
  );

  const filteredTransactions = useMemo(
    () => filterTransactionsByMonth(allTransactions, appliedMonth),
    [allTransactions, appliedMonth],
  );

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    axios
      .get(`/v1/cre/tutor?tutorId=${id}`)
      .then((res) => {
        const tutorData = res?.data?.data?.Result?.[0] || null;
        setTutor(tutorData);

        if (!tutorData?.user_id) {
          setEarnings(null);
          setLoading(false);
          return;
        }

        return axios.get(`/v1/cre/tutor/earnings?userId=${tutorData.user_id}`);
      })
      .then((earningsRes) => {
        if (!earningsRes) return;
        if (earningsRes?.data?.is_success) {
          setEarnings(earningsRes.data.data || null);
        } else {
          setEarnings(null);
        }
      })
      .catch(() => {
        setTutor(null);
        setEarnings(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const earningsRows = useMemo(
    () => mapTransactionsToRows(filteredTransactions),
    [filteredTransactions],
  );

  const earningsSummary = useMemo(() => {
    const apiTotal = earnings?.total ?? earnings?.total_earnings ?? 0;
    const apiLastMonth = earnings?.last_month ?? earnings?.lastMonth ?? 0;

    if (!appliedMonth) {
      return {
        total: apiTotal,
        lastMonth: apiLastMonth,
        reportMonthLabel: "All time",
        periodLabel: "All transferred payouts",
      };
    }

    const periodTotal = sumAmount(filteredTransactions);
    const previousMonth = appliedMonth.subtract(1, "month");
    const previousMonthTotal = sumAmount(
      filterTransactionsByMonth(allTransactions, previousMonth),
    );

    return {
      total: periodTotal,
      lastMonth: previousMonthTotal,
      reportMonthLabel: formatReportMonth(appliedMonth),
      periodLabel: `Payouts in ${formatReportMonth(appliedMonth)}`,
    };
  }, [earnings, appliedMonth, filteredTransactions, allTransactions]);

  const isThisMonthActive =
    appliedMonth &&
    appliedMonth.year() === dayjs().year() &&
    appliedMonth.month() === dayjs().month();

  const isLastMonthActive =
    appliedMonth &&
    appliedMonth.year() === dayjs().subtract(1, "month").year() &&
    appliedMonth.month() === dayjs().subtract(1, "month").month();

  const applyMonthFilter = useCallback((month) => {
    setAppliedMonth(month);
    setDraftMonth(month || dayjs());
  }, []);

  const handleThisMonth = () => applyMonthFilter(dayjs().startOf("month"));
  const handleLastMonth = () => applyMonthFilter(dayjs().subtract(1, "month").startOf("month"));
  const handleClearFilter = () => applyMonthFilter(null);

  const handleReportMonthChange = (value) => {
    if (!value) {
      handleClearFilter();
      return;
    }
    applyMonthFilter(value.startOf("month"));
  };

  const openFilterModal = () => {
    setDraftMonth(appliedMonth || dayjs());
    setFilterModalOpen(true);
  };

  const handleFilterApply = () => {
    if (!draftMonth) {
      message.warning("Select a report month");
      return;
    }
    applyMonthFilter(draftMonth.startOf("month"));
    setFilterModalOpen(false);
  };

  const handleExport = () => {
    if (!earningsRows.length) {
      message.warning("No earnings data to export for the current filter");
      return;
    }

    const tutorName = `${tutor?.first_name || ""} ${tutor?.last_name || ""}`.trim() || `tutor-${id}`;
    const period = appliedMonth ? appliedMonth.format("YYYY-MM") : "all-time";
    const filename = `earnings-${tutorName.replace(/\s+/g, "-")}-${period}.csv`;

    try {
      downloadCsv(earningsRows, filename);
      message.success(`Exported ${earningsRows.length} record(s)`);
    } catch {
      message.error("Failed to export earnings data");
    }
  };

  const canGoPrev = Boolean(appliedMonth);
  const canGoNext = Boolean(
    appliedMonth &&
      appliedMonth.startOf("month").isBefore(dayjs().startOf("month"), "month"),
  );

  const handlePrevMonth = () => {
    if (!appliedMonth) return;
    applyMonthFilter(appliedMonth.subtract(1, "month").startOf("month"));
  };

  const handleNextMonth = () => {
    if (!appliedMonth || !canGoNext) return;
    applyMonthFilter(appliedMonth.add(1, "month").startOf("month"));
  };

  const earningsColumns = [
    { title: "Deal ID", dataIndex: "dealId", key: "dealId", width: 110 },
    { title: "Parent Name", dataIndex: "parentName", key: "parentName", width: 160 },
    { title: "Subjects", dataIndex: "subjects", key: "subjects", width: 210 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (value) => (
        <span className="earnings-status-pill" style={{ color: "#70cf32" }}>
          {value}
        </span>
      ),
    },
    {
      title: "Amount Earned",
      dataIndex: "amountEarned",
      key: "amountEarned",
      width: 140,
      render: (value) => (value ? formatINR(value) : "-"),
    },
    { title: "Date", dataIndex: "date", key: "date", width: 130 },
    { title: "No of Teaching Hours", dataIndex: "teachingHours", key: "teachingHours", width: 180 },
    { title: "Fees", dataIndex: "fees", key: "fees", width: 90 },
  ];

  return (
    <main className="tail-p-6 tail-max-w-screen-xl tail-mx-auto tail-min-h-screen tail-bg-white">
      <section className="section-title-edit-wrapper" style={{ marginTop: 0 }}>
        <div className="section-title-container">
          {`Account Details of ${tutor?.id ?? id} - ${tutor?.user_id ?? "-"} > Earnings report`}
        </div>
        <Button onClick={() => navigate(`/tutor/${id}`)}>Back to Tutor</Button>
      </section>

      <Spin spinning={loading}>
        <section className="earnings-report-wrapper">
          <div className="earnings-top-row">
            <div>
              <div className="earnings-label">
                {appliedMonth ? "Earnings for selected period" : "Tutor earnings summary"}
              </div>
              <div className="earnings-big">{formatINR(earningsSummary.total)}</div>
            </div>
            <Tag color="green" className="earnings-month-tag">
              <CalendarOutlined /> {earningsSummary.reportMonthLabel}
            </Tag>
          </div>

          <div className="earnings-last-month-box">
            <div className="earnings-label">
              {appliedMonth ? "Previous month (same tutor)" : "Last month earnings"}
            </div>
            <div className="earnings-mid">{formatINR(earningsSummary.lastMonth)}</div>
            <div className="earnings-subtext">{earningsSummary.periodLabel}</div>
          </div>

          <div className="earnings-filters-row">
            <div className="earnings-filter-item">
              <div className="earnings-filter-label">Tutor</div>
              <Input value={`${tutor?.first_name || ""} ${tutor?.last_name || ""}`.trim()} readOnly />
            </div>
            <div className="earnings-filter-item">
              <div className="earnings-filter-label">Report month</div>
              <DatePicker
                picker="month"
                value={appliedMonth}
                onChange={handleReportMonthChange}
                format="MMMM YYYY"
                allowClear
                placeholder="All time"
                style={{ width: "100%" }}
              />
            </div>
            <div className="earnings-filter-item earnings-quick-filter">
              <div className="earnings-filter-label">Quick filters</div>
              <Space wrap>
                <Button type={isThisMonthActive ? "primary" : "default"} onClick={handleThisMonth}>
                  This month
                </Button>
                <Button type={isLastMonthActive ? "primary" : "default"} onClick={handleLastMonth}>
                  Last month
                </Button>
                <Button type={!appliedMonth ? "primary" : "default"} onClick={handleClearFilter}>
                  All time
                </Button>
              </Space>
            </div>
            <Space className="earnings-actions">
              <Button icon={<FilterOutlined />} onClick={openFilterModal}>
                Filter
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!earningsRows.length}>
                Export
              </Button>
            </Space>
          </div>

          <Table
            className="earnings-table"
            columns={earningsColumns}
            dataSource={earningsRows}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
            scroll={{ x: 1150 }}
            locale={{ emptyText: "No transferred earnings found for this filter" }}
          />

          <div className="earnings-table-footer">
            <span>
              Showing {earningsRows.length} payout record{earningsRows.length === 1 ? "" : "s"}
              {appliedMonth ? ` for ${formatReportMonth(appliedMonth)}` : " (all time)"}
            </span>
            <Space>
              <Button disabled={!canGoPrev} onClick={handlePrevMonth}>
                Previous
              </Button>
              <Button disabled={!canGoNext} onClick={handleNextMonth}>
                Next
              </Button>
            </Space>
          </div>
        </section>
      </Spin>

      <Modal
        title="Filter earnings by month"
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        footer={[
          <Button key="clear" onClick={() => { handleClearFilter(); setFilterModalOpen(false); }}>
            Show all
          </Button>,
          <Button key="cancel" onClick={() => setFilterModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleFilterApply}>
            Apply
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 8, color: "#8d8d8d" }}>Report month</div>
        <DatePicker
          picker="month"
          value={draftMonth}
          onChange={(value) => setDraftMonth(value || dayjs())}
          format="MMMM YYYY"
          style={{ width: "100%" }}
          disabledDate={(current) => current && current.isAfter(dayjs(), "month")}
        />
      </Modal>
    </main>
  );
}
